import { useState } from 'react'
import { colors, fonts, sentiment } from './theme'
import { INSTITUTIONS, CREDIT_SECTORS } from './constants'
import MetricCard from './components/MetricCard'
import SectionBar from './components/SectionBar'

export default function InstitutionAnalysis() {
  const [selectedTicker, setSelectedTicker] = useState('OWL')
  const institutions = Object.values(INSTITUTIONS)
  const selected = INSTITUTIONS[selectedTicker]

  return (
    <div style={styles.page}>
      {/* Left sidebar: institution list */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>Institutions</div>
        {institutions.map(inst => (
          <button
            key={inst.ticker}
            onClick={() => setSelectedTicker(inst.ticker)}
            style={{
              ...styles.sidebarItem,
              ...(selectedTicker === inst.ticker ? styles.sidebarItemActive : {}),
            }}
          >
            <span style={styles.sidebarTicker}>{inst.ticker}</span>
            <span style={styles.sidebarName}>{inst.name}</span>
          </button>
        ))}
      </div>

      {/* Main content: institution deep dive */}
      <div style={styles.main}>
        <InstitutionDetail institution={selected} />
      </div>
    </div>
  )
}

function InstitutionDetail({ institution }) {
  if (!institution) return null

  return (
    <div style={styles.detail}>
      <div style={styles.detailHeader}>
        <div>
          <div style={styles.detailTicker}>{institution.ticker}</div>
          <div style={styles.detailName}>{institution.name}</div>
          <div style={styles.detailType}>{institution.type}</div>
        </div>
      </div>

      {/* Key Metrics — awaiting real data */}
      <SectionBar title="Key Metrics" subtitle="Awaiting live data integration" />

      <div style={styles.metricsRow}>
        <MetricCard label="Share Price" value="—" sublabel="Connect data source" />
        <MetricCard label="Market Cap" value="—" sublabel="Connect data source" />
        <MetricCard label="Dividend Yield" value="—" sublabel="Connect data source" />
        <MetricCard label="NAV Premium/Discount" value="—" sublabel="Connect data source" />
        <MetricCard label="Portfolio Yield" value="—" sublabel="Connect data source" />
      </div>

      {/* Fundamental Analysis Framework */}
      <SectionBar title="Due Diligence Framework" subtitle="Ackman-level rigor" accent="gold" />

      <div style={styles.frameworkGrid}>
        <FrameworkSection
          title="Portfolio Quality"
          items={[
            'Senior secured % of portfolio',
            'First lien vs. second lien mix',
            'Average credit quality (internal ratings)',
            'Non-accrual rate (% on non-performing)',
            'Sector concentration / diversification',
            'Average loan size / borrower count',
          ]}
        />
        <FrameworkSection
          title="Earnings Power"
          items={[
            'Net investment income (NII) per share',
            'NII coverage of dividend (spillover income)',
            'Management & incentive fee structure',
            'Net asset value (NAV) trend',
            'Realized vs. unrealized gains/losses',
            'Fee waiver history and duration',
          ]}
        />
        <FrameworkSection
          title="Leverage & Funding"
          items={[
            'Regulatory leverage ratio (debt/equity)',
            'Asset coverage ratio',
            'Funding mix (secured vs. unsecured)',
            'Weighted average cost of debt',
            'Maturity schedule / refinancing risk',
            'Unfunded commitment capacity',
          ]}
        />
        <FrameworkSection
          title="Management Quality"
          items={[
            'Track record through credit cycles',
            'Alignment (insider ownership)',
            'Investment committee composition',
            'Deal origination capabilities',
            'Workout / restructuring expertise',
            'Conflicts of interest (affiliated funds)',
          ]}
        />
      </div>


      {/* MSCI-Inspired Analysis Framework */}
      <SectionBar title="Risk Decomposition" subtitle="MSCI Private Credit Factor Model approach" />

      <div style={styles.riskDecomp}>
        <RiskFactor
          label="Public Factors"
          items={['Credit spreads', 'Term structure / rates', 'Implied volatility', 'Market / style factors']}
          color={colors.steel}
        />
        <RiskFactor
          label="Private Factors"
          items={['Pure private credit exposure', 'PE exposure / equity kickers', 'Illiquidity premium', 'Manager alpha']}
          color={colors.gold}
        />
      </div>

      <SectionBar title="Fund Lifecycle Analysis" subtitle="How portfolios drift over time" />

      <div style={styles.lifecycleGrid}>
        <LifecycleStage
          stage="Young (0-2 years)"
          characteristics={[
            'Risk dominated by credit spreads',
            '~Two-thirds of risk from public factors',
            'Highest correlation to total portfolio (0.64)',
            'Lowest diversification benefit',
          ]}
          color={colors.steel}
        />
        <LifecycleStage
          stage="Mature Diversified (3-5 years)"
          characteristics={[
            'Risk from private credit factors',
            'Minimal spread sensitivity',
            'Correlation 0.25 to total portfolio',
            'Best diversifier in portfolio',
          ]}
          color={colors.greenInk}
        />
        <LifecycleStage
          stage="Concentrated Tail (5+ years)"
          characteristics={[
            'Over half of risk in handful of positions',
            'Correlation 0.22 to total portfolio',
            'Highest standalone risk',
            'Uncorrelated but concentrated',
          ]}
          color={colors.amberInk}
        />
      </div>
    </div>
  )
}

// Sub-components
function FrameworkSection({ title, items }) {
  return (
    <div style={styles.frameworkCard}>
      <div style={styles.frameworkTitle}>{title}</div>
      {items.map((item, i) => (
        <div key={i} style={styles.frameworkItem}>
          <span style={styles.frameworkBullet}>{i + 1}.</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  )
}

function RiskFactor({ label, items, color }) {
  return (
    <div style={{ ...styles.riskCard, borderLeftColor: color }}>
      <div style={{ ...styles.riskLabel, color }}>{label}</div>
      {items.map((item, i) => (
        <div key={i} style={styles.riskItem}>{item}</div>
      ))}
    </div>
  )
}

function LifecycleStage({ stage, characteristics, color }) {
  return (
    <div style={{ ...styles.lifecycleCard, borderTopColor: color }}>
      <div style={{ ...styles.lifecycleStage, color }}>{stage}</div>
      {characteristics.map((c, i) => (
        <div key={i} style={styles.lifecycleItem}>
          <span style={{ color }}>&#x2022;</span> {c}
        </div>
      ))}
    </div>
  )
}

const styles = {
  page: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  sidebar: {
    width: 220,
    borderRight: `1px solid ${colors.rule}`,
    background: colors.navyDeep,
    overflow: 'auto',
    flexShrink: 0,
  },
  sidebarHeader: {
    padding: '12px 16px',
    fontFamily: fonts.serif,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: colors.textMuted,
    borderBottom: `1px solid ${colors.rule}`,
  },
  sidebarItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    padding: '10px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: `1px solid ${colors.ruleLight}`,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.1s',
  },
  sidebarItemActive: {
    background: colors.steelBg,
    borderLeft: `2px solid ${colors.steel}`,
  },
  sidebarTicker: {
    fontFamily: fonts.mono,
    fontSize: 12,
    fontWeight: 600,
    color: colors.steel,
  },
  sidebarName: {
    fontFamily: fonts.sans,
    fontSize: 11,
    color: colors.textMuted,
  },
  main: {
    flex: 1,
    overflow: 'auto',
  },
  detail: {
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  detailTicker: {
    fontFamily: fonts.mono,
    fontSize: 28,
    fontWeight: 700,
    color: colors.steel,
  },
  detailName: {
    fontFamily: fonts.serif,
    fontSize: 18,
    color: colors.textPrimary,
    marginTop: 2,
  },
  detailType: {
    fontFamily: fonts.mono,
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 4,
  },
  metricsRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  // Framework
  frameworkGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12,
  },
  frameworkCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  frameworkTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 10,
  },
  frameworkItem: {
    display: 'flex',
    gap: 8,
    padding: '4px 0',
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
  },
  frameworkBullet: {
    fontFamily: fonts.mono,
    color: colors.textMuted,
    minWidth: 18,
  },
  // Risk decomposition
  riskDecomp: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  riskCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderLeft: '3px solid',
    borderRadius: 3,
  },
  riskLabel: {
    fontFamily: fonts.serif,
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  riskItem: {
    padding: '4px 0',
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
  },
  // Lifecycle
  lifecycleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  lifecycleCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderTop: '3px solid',
    borderRadius: 3,
  },
  lifecycleStage: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 10,
  },
  lifecycleItem: {
    padding: '3px 0',
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
  },
}
